import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  acceptInvitationResultSchema,
  claimInvitationResultSchema,
  type InvitationPreview,
} from '@forecastify/shared';
import { Button, SetupActions, SetupFrame, SetupStep } from '@forecastify/ui';
import logo from '@forecastify/ui/assets/forecastify-logo.png';
import logoWhite from '@forecastify/ui/assets/forecastify-logo-white.png';
import { apiUrl } from '../../api';
import { useI18n } from '../../i18n';
import { useSession } from '../../session';
import { forgetAvatars } from '../use-avatar';
import { ProfileStep, profileBlocked, type ProfileValues } from '../setup/profile-step';
import { SetupPreview } from '../setup/setup-preview';
import { pendingAvatar } from '../setup/setup-store';
import { personInitials, personName } from '../setup/setup-rules';
import { useObjectUrl } from '../setup/use-object-url';
import { claimMessage, outcomeMessage } from './invite-messages';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Setup step 1, on the invitation page — the account, made here.            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * For somebody with no account holding a live link: an operator's invitation to set up an
 * organisation (step 1 of 4 — the setup screen takes over at step 2) or a colleague's invitation
 * into a workspace (the only step; the dashboard is next). Somebody who already has an account
 * signs in instead and never sees this (flow D).
 *
 * ⚠️ **The same three calls the page always made, in the same order**: claim (creates the account
 * with this password), sign in, accept. Accepting now carries the name — `0202` — so nobody joins
 * under their address any more.
 */
export function InviteProfile({
  preview,
  token,
}: {
  readonly preview: InvitationPreview;
  readonly token: string;
}): ReactNode {
  const { t, locale } = useI18n();
  const session = useSession();
  const navigate = useNavigate();
  const organisation = preview.kind === 'organisation';

  const [values, setValues] = useState<ProfileValues>({
    family: preview.familyName ?? '',
    given: preview.givenName ?? '',
    avatar: null,
    password: '',
    confirmation: '',
  });
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const avatarSrc = useObjectUrl(values.avatar);
  const reason = profileBlocked(values, t);

  /**
   * ⚠️ A colleague's workspace exists the moment they accept, so the picture goes there at once —
   * with the new workspace's id as the header, which `session.tsx` lets win. Read from `/auth/me`
   * rather than the session, whose state this closure would see a render late.
   */
  const uploadAvatar = async (slug: string, file: File): Promise<void> => {
    const me = await session.request('/auth/me').catch(() => null);
    const body = (await me?.json().catch(() => null)) as {
      workspaces?: { id?: string; slug?: string }[];
    } | null;
    const id = body?.workspaces?.find((entry) => entry.slug === slug)?.id;
    if (id === undefined) return;
    const form = new FormData();
    form.append('file', file);
    await session
      .request('/files?target=user_avatar&targetId=me', {
        method: 'POST',
        body: form,
        headers: { 'x-workspace-id': id },
      })
      .catch(() => null);
    forgetAvatars();
  };

  const submit = (): void => {
    if (busy || reason !== null) return;
    setBusy(true);
    setFailure(null);
    void (async () => {
      /* ⚠️ Plain `fetch`: there is no session yet — this is what creates the account. */
      const claimed = await fetch(apiUrl(`/invitations/${encodeURIComponent(token)}/claim`), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ password: values.password }),
      }).catch(() => null);
      const claim = claimInvitationResultSchema.safeParse(
        claimed === null ? null : await claimed.json().catch(() => null),
      );
      if (!claim.success) {
        setBusy(false);
        setFailure(t('error.unavailable'));
        return;
      }
      if (claim.data.outcome !== 'created') {
        setBusy(false);
        setFailure(claimMessage(t, claim.data.outcome));
        return;
      }

      /* The address is the INVITATION'S — the only one the server will accept for this link. */
      const signedIn = await session.signIn(preview.email, values.password);
      if (!signedIn.ok) {
        setBusy(false);
        setFailure(t('invite.claim.signInFailed'));
        return;
      }

      const accepted = await session
        .request(`/invitations/${encodeURIComponent(token)}/accept`, {
          method: 'POST',
          body: JSON.stringify({
            familyName: values.family.trim(),
            givenName: values.given.trim(),
          }),
        })
        .catch(() => null);
      const result = acceptInvitationResultSchema.safeParse(
        accepted?.ok === true ? await accepted.json().catch(() => null) : null,
      );
      if (!result.success) {
        setBusy(false);
        setFailure(t('error.unavailable'));
        return;
      }
      if (result.data.outcome !== 'joined' && result.data.outcome !== 'already_member') {
        setBusy(false);
        setFailure(outcomeMessage(t, result.data.outcome));
        return;
      }

      if (organisation) {
        /* Step 2 makes the workspace the picture is filed under — `setup-store.ts`. */
        pendingAvatar.hold(values.avatar);
      } else if (values.avatar !== null && result.data.workspaceSlug !== null) {
        await uploadAvatar(result.data.workspaceSlug, values.avatar);
      }
      await session.reload();
      setBusy(false);
      void navigate('/');
    })();
  };

  return (
    <SetupFrame
      screen="invite"
      logo={{ src: logo, darkSrc: logoWhite, alt: t('app.name') }}
      step={1}
      total={organisation ? 4 : 1}
      stepLabel={t('setup.stepOf', { step: 1, total: organisation ? 4 : 1 })}
      onSubmit={submit}
      footnote={
        <button
          type="button"
          className="hover:text-foreground underline-offset-2 hover:underline"
          onClick={() => {
            void navigate('/sign-in');
          }}
        >
          {t('setup.profile.haveAccount')}
        </button>
      }
      aside={
        <SetupPreview
          focus="profile"
          sharp={false}
          name={personName(values.family, values.given, locale)}
          initials={personInitials(values.family, values.given, locale)}
          email={preview.email}
          avatarSrc={avatarSrc}
          workspaceName={preview.workspaceName}
          logoSrc={null}
          invitees={[]}
        />
      }
    >
      <SetupStep>
        <ProfileStep
          values={values}
          onChange={setValues}
          avatarSrc={avatarSrc}
          avatarError={avatarError}
          onAvatarError={setAvatarError}
          email={preview.email}
        />
      </SetupStep>
      <SetupActions
        primary={
          <Button
            type="submit"
            className="h-9 gap-2.5 px-4 text-[13px]"
            disabled={busy || reason !== null}
          >
            {busy ? t('setup.working') : t('setup.continue')}
            <span aria-hidden="true" className="text-[12px] opacity-60">
              ↵
            </span>
          </Button>
        }
        reason={failure ?? reason ?? ''}
      />
    </SetupFrame>
  );
}
